import * as React from 'react';
import CompareIcon from '@mui/icons-material/Compare';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { canCompareImages, MAX_COMPARE_IMAGES } from '../../../shared/compareImages/state/utils';
import ConfirmDialog from '../../../shared/dialog/view/ConfirmDialog';
import {
    ImageSelectionToolbar,
    SelectionAction
} from '../../../shared/selection/view/ImageSelectionToolbar';
import { ImageEditorPhotoStatus } from '../../entry/queries/use-query-photo-status';
import { useDeleteImagesMutation } from '../queries/use-mutation-delete-images';
import { useDownloadImagesMutation } from '../queries/use-mutation-download-images';
import { canDeleteEditedImages, canDownloadUneditedImages } from '../state/utils';

export enum SelectImagesVariant {
    Unedited = 'unedited',
    Edited = 'edited'
}

type SelectableImage = {
    id: number;
    canDelete: boolean;
};

type SelectImagesToolbarProps = {
    orderId: number;
    variant: SelectImagesVariant;
    user: AuthenticatedUser;
    photoStatus: ImageEditorPhotoStatus;
    images: SelectableImage[];
    isSelecting: boolean;
    selectedImageIds: Set<number>;
    onSelectStart: () => void;
    onSelectAll: (imageIds: number[]) => void;
    onDeselectAll: () => void;
    onCancel: () => void;
    onCompare?: () => void;
    onImagesDeleted?: (imageIds: number[]) => void;
};

export function SelectImagesToolbar({
    orderId,
    variant,
    user,
    photoStatus,
    images,
    isSelecting,
    selectedImageIds,
    onSelectStart,
    onSelectAll,
    onDeselectAll,
    onCancel,
    onCompare,
    onImagesDeleted
}: SelectImagesToolbarProps) {
    let deleteImages = useDeleteImagesMutation({ orderId, user });
    let downloadImages = useDownloadImagesMutation({ orderId, user });
    let [isConfirmingDelete, setIsConfirmingDelete] = React.useState(false);
    let hasSelection = selectedImageIds.size > 0;
    let isBusy = deleteImages.isLoading || downloadImages.isLoading;
    let imageIds = images.map((image) => image.id);
    let isAllSelected = images.length > 0 && selectedImageIds.size === images.length;
    let isEveryImageDeletable = images
        .filter((image) => selectedImageIds.has(image.id))
        .every((image) => image.canDelete);

    if (variant === SelectImagesVariant.Unedited && !canDownloadUneditedImages(user)) {
        return null;
    }

    let downloadAction: SelectionAction = {
        label: Translate('order/image-processing/button-toolbar/button/download'),
        icon: <FileDownloadIcon />,
        disabled: !hasSelection || isBusy,
        testId: 'download-selected-images',
        onClick: () => downloadImages.mutate(Array.from(selectedImageIds), { onSuccess: onCancel })
    };

    let deleteAction: SelectionAction = {
        label: Translate('image-processing/button-label/delete-multiple/edit'),
        icon: <DeleteIcon />,
        disabled: !hasSelection || !isEveryImageDeletable || isBusy,
        color: 'error',
        testId: 'delete-selected-images',
        onClick: () => setIsConfirmingDelete(true)
    };

    let compareAction: SelectionAction = {
        label: Translate('order/image-processing/button-toolbar/button/compare'),
        icon: <CompareIcon />,
        disabled: !canCompareImages(selectedImageIds.size),
        disabledTooltip:
            selectedImageIds.size > MAX_COMPARE_IMAGES
                ? Translate('order/image-processing/button-toolbar/tooltip/compare-too-many', {
                      count: MAX_COMPARE_IMAGES
                  })
                : Translate('order/image-processing/button-toolbar/tooltip/compare-disabled'),
        testId: 'compare-selected-images',
        onClick: () => onCompare()
    };

    let isEditedVariant = variant === SelectImagesVariant.Edited;
    let canCompare = isEditedVariant && onCompare !== undefined;
    let canDelete = isEditedVariant && canDeleteEditedImages(user, photoStatus);
    let actions = [
        ...(canCompare ? [compareAction] : []),
        downloadAction,
        ...(canDelete ? [deleteAction] : [])
    ];

    function handleConfirmDelete() {
        let deletedImageIds = Array.from(selectedImageIds);
        setIsConfirmingDelete(false);
        if (onImagesDeleted) {
            onImagesDeleted(deletedImageIds);
        }
        deleteImages.mutateAsync(deletedImageIds).then(onCancel);
    }

    let confirmationText =
        selectedImageIds.size === 1
            ? Translate('image-processing/modal/delete-edited-photo/confirmation-modal/text')
            : Translate('image-processing/delete-multiple/confirmation-modal/text', {
                  count: selectedImageIds.size
              });

    return (
        <>
            <ImageSelectionToolbar
                isSelecting={isSelecting}
                isSelectDisabled={images.length === 0}
                onSelectStart={onSelectStart}
                onCancel={onCancel}
                selectTooltip={Translate('image-processing/button-tooltip/select-many/info')}
                actions={actions}
                selectAll={{
                    isAllSelected,
                    onSelectAll: () => onSelectAll(imageIds),
                    onDeselectAll
                }}
            />
            <ConfirmDialog
                open={isConfirmingDelete}
                title={Translate(
                    'image-processing/modal/delete-edited-photo/confirmation-modal/title'
                )}
                text={confirmationText}
                confirmLabel={Translate(
                    'image-processing/modal/delete-edited-photo/confirmation-modal/do-button'
                )}
                cancelLabel={Translate(
                    'image-processing/modal/delete-edited-photo/confirmation-modal/cancel-button'
                )}
                onConfirm={handleConfirmDelete}
                onCancel={() => setIsConfirmingDelete(false)}
            />
        </>
    );
}
